/**
 * TeamMonthlyDataService - 공통 서비스 레이어
 *
 * ActualSalesService와 DailyReportPage의 팀별 월간 데이터 계산 로직을 통합
 * 일관된 팀 매핑 및 집계 방식을 보장
 */

import { Employee } from '../contexts/AppContext.tsx';
import { accumulateSourceMax, SourceMaxMap } from '../utils/monthlyAggregation.ts';

// ═══════════════════════════════════════════════════
// 인터페이스 정의
// ═══════════════════════════════════════════════════

/**
 * 팀별 월간 데이터
 */
export interface TeamMonthlyData {
  teamName: string;
  monthlyReceipt: number;   // 수납액
  monthlyExpense: number;   // 지출액
  netProfit: number;        // 순수익
  assistCount: number;      // 어시스트 수
}

/**
 * 원본 어시스트 레코드 (집계 전)
 */
export interface RawAssistRecord {
  assistName: string;
  date: string;             // YYYY-MM-DD
  receipts: number;
  expenses: number;
  compositeKey: string;     // 원본 compositeKey
  isFinancialTransaction: boolean;
  cashierName?: string;     // Financial용 수납자명 (1.5순위 폴백)
  assistTeam?: string;      // Financial용 assistTeam (2순위 폴백)
  teamName?: string;        // Financial용 teamName (2.5순위 폴백)
  team?: string;            // 원본 item.team (4순위 폴백)
}

/**
 * 어시스트별 상세 데이터 (집계 후)
 * ActualSalesService의 AssistDetailData와 동일한 구조
 */
export interface AssistDetailData {
  receipts: number;
  expenses: number;
  profit: number;
  teamName: string;
  compositeKey?: string;
}

/**
 * 집계 전략 타입
 */
export type AggregationStrategy = 'max' | 'sum';

// ═══════════════════════════════════════════════════
// 핵심 함수 구현
// ═══════════════════════════════════════════════════

/**
 * 1. 팀 이름 정규화
 * @param teamName 원본 팀 이름
 * @returns 정규화된 팀 이름
 */
export function normalizeTeamName(teamName: string): string {
  if (!teamName) return '미배정';

  const normalized = teamName.trim();

  // 팀명 매핑 테이블
  const teamMappings: Record<string, string> = {
    '1팀': '1팀',
    '2팀': '2팀',
    '3팀': '3팀',
    '4팀': '4팀',
    '5팀': '5팀',
    '외과': '외과',
    '내과': '내과',
    '피부과': '피부과',
    '성형외과': '성형외과',
    '미지정': '미지정',
    '미배정': '미배정',
  };

  return teamMappings[normalized] || normalized;
}

/**
 * 2. budgetData 우선 직원 선택
 * budgetData 월별 직원 우선, 없으면 전역 employees 사용
 * (ActualSalesService 라인 43-56과 동일)
 *
 * @param employees 전역 직원 데이터
 * @param budgetData 예산 데이터 (월별 직원 포함)
 * @param yearMonth 조회할 년월 (YYYY-MM)
 * @returns 선택된 직원 배열
 */
export function selectTargetEmployees(
  employees: Employee[],
  budgetData: any | null,
  yearMonth: string
): Employee[] {
  if (budgetData) {
    // ✅ 실제 경로: budgetData.monthlyData?.[yearMonth]?.employees
    const monthlyEmployees = budgetData.monthlyData?.[yearMonth]?.employees;
    if (monthlyEmployees && monthlyEmployees.length > 0) {
      console.log(`[TeamMonthlyDataService] ${yearMonth} 월별 저장 직원 데이터 사용 (${monthlyEmployees.length}명)`);
      return monthlyEmployees;
    }
  }
  console.log(`[TeamMonthlyDataService] ${yearMonth} 전역 직원 데이터 사용 (${employees.length}명)`);
  return employees;
}

/**
 * 3. 직원-팀 매핑 생성
 * @param targetEmployees 대상 직원 배열
 * @returns 직원명 → 팀명 매핑 객체
 */
export function buildEmployeeTeamMap(
  targetEmployees: Employee[]
): Record<string, string> {
  const employeeTeamMap: Record<string, string> = {};

  targetEmployees.forEach(emp => {
    employeeTeamMap[emp.name] = emp.team || '미지정';
  });

  // 디버그 로깅 (샘플 5개)
  const sampleEntries = Object.entries(employeeTeamMap).slice(0, 5);
  console.log(`[TeamMonthlyDataService] employeeTeamMap 생성 완료 (${Object.keys(employeeTeamMap).length}명)`,
    sampleEntries.map(([name, team]) => `${name}→${team}`)
  );

  return employeeTeamMap;
}

/**
 * 4. 어시스트명으로 팀 조회 (5단계 폴백, TeamCollectionService 제외)
 *
 * 우선순위:
 * 1순위: employeeTeamMap[assistName]
 * 1.5순위: employeeTeamMap[cashierName] (폴백)
 * 2순위: Financial의 assistTeam 필드
 * 2.5순위: Financial의 teamName 필드
 * 3순위: TeamCollectionService - async이므로 공통 서비스에서 제외
 * 4순위: item.team 필드
 * 5순위: '미지정' (기본값)
 *
 * @param assistName 어시스트 이름
 * @param employeeTeamMap 직원-팀 매핑
 * @param cashierName 수납자 이름 (Financial용)
 * @param assistTeam assistTeam 필드 (Financial용)
 * @param teamName teamName 필드 (Financial용)
 * @param itemTeam item.team 필드
 * @returns 매핑된 팀 이름
 */
export function getTeamForAssist(
  assistName: string,
  employeeTeamMap: Record<string, string>,
  cashierName?: string,
  assistTeam?: string,
  teamName?: string,
  itemTeam?: string
): string {
  // 1순위: employeeTeamMap[assistName]
  if (employeeTeamMap[assistName] && employeeTeamMap[assistName] !== '미지정') {
    return employeeTeamMap[assistName];
  }

  // 1.5순위: employeeTeamMap[cashierName] (폴백)
  if (cashierName && employeeTeamMap[cashierName] && employeeTeamMap[cashierName] !== '미지정') {
    return employeeTeamMap[cashierName];
  }

  // 2순위: Financial의 assistTeam 필드
  if (assistTeam && assistTeam !== '미지정') {
    return assistTeam.trim();
  }

  // 2.5순위: Financial의 teamName 필드
  if (teamName && teamName !== '미지정') {
    return teamName.trim();
  }

  // 3순위: TeamCollectionService 생략 (async, DailyReportPage에 불필요)
  // ActualSalesService에서만 직접 호출하도록 유지

  // 4순위: item.team 필드
  if (itemTeam && itemTeam !== '미지정') {
    return itemTeam.trim();
  }

  // 5순위: 기본값
  return '미지정';
}

/**
 * 5. 어시스트-팀 키 생성
 *
 * @param assistName 어시스트 이름
 * @param teamName 팀 이름
 * @param isFinancial Financial 거래 여부
 * @returns 생성된 키 ("어시스트명(팀명)" 또는 "어시스트명(팀명)-financial")
 */
export function buildAssistTeamKey(
  assistName: string,
  teamName: string,
  isFinancial: boolean
): string {
  const baseKey = `${assistName}(${teamName})`;
  return isFinancial ? `${baseKey}-financial` : baseKey;
}

/**
 * 6. 어시스트별 데이터 집계
 * - Financial (-financial 접미사): += 합산
 * - 일반 teamCollection: Math.max() 비교
 *
 * @param rawRecords 원본 레코드 배열
 * @param employeeTeamMap 직원-팀 매핑
 * @returns 집계된 어시스트별 상세 데이터
 */
export function aggregateAssistData(
  rawRecords: RawAssistRecord[],
  employeeTeamMap: Record<string, string>
): Record<string, AssistDetailData> {
  const assistDetails: Record<string, AssistDetailData> = {};
  // 🛡️ 버킷별 출처(원본 compositeKey)별 MAX 맵 — 총합 = Σ(출처별 MAX) 보장용
  const sourceMaxByBucket: Record<string, SourceMaxMap> = {};

  for (const record of rawRecords) {
    // 팀 매핑 (5단계 폴백, TeamCollectionService 제외)
    const mappedTeamName = getTeamForAssist(
      record.assistName,
      employeeTeamMap,
      record.cashierName,
      record.assistTeam,
      record.teamName,
      record.team
    );

    // 키 생성
    const assistTeamKey = buildAssistTeamKey(
      record.assistName,
      mappedTeamName,
      record.isFinancialTransaction
    );

    if (assistDetails[assistTeamKey]) {
      const existing = assistDetails[assistTeamKey];
      const isFinancial = assistTeamKey.includes('-financial');

      if (isFinancial) {
        // ✅ Financial: 날짜별 개별 거래이므로 합산 (+=)
        existing.receipts += record.receipts;
        existing.expenses += record.expenses;
        existing.profit = existing.receipts - existing.expenses;
      } else {
        // 📊 일반: 같은 출처(원본 compositeKey)는 월 누적값이므로 MAX, 다른 출처(팀 재매핑 전후 등)는 SUM
        // — 출처별 MAX 맵으로 총합 = Σ(출처별 MAX) 보장 ('직전 키 1개 기억'은 A→B→A 교차 도착 시 이중계상)
        const sources = sourceMaxByBucket[assistTeamKey] || (sourceMaxByBucket[assistTeamKey] = {});
        const delta = accumulateSourceMax(sources, record.compositeKey || '', record.receipts, record.expenses);
        existing.receipts += delta.receipts;
        existing.expenses += delta.expenses;
        existing.profit = existing.receipts - existing.expenses;
      }
    } else {
      // 새 어시스트 추가 (비Financial은 출처별 MAX 맵에 기준값 등록)
      if (!record.isFinancialTransaction) {
        accumulateSourceMax((sourceMaxByBucket[assistTeamKey] = {}), record.compositeKey || '', record.receipts, record.expenses);
      }
      assistDetails[assistTeamKey] = {
        receipts: record.receipts,
        expenses: record.expenses,
        profit: record.receipts - record.expenses,
        teamName: mappedTeamName,
        compositeKey: record.compositeKey
      };
    }
  }

  console.log(`[TeamMonthlyDataService] aggregateAssistData 완료: ${Object.keys(assistDetails).length}개 어시스트`);
  return assistDetails;
}

/**
 * 7. 팀별 그룹화
 * 어시스트별 상세 데이터를 팀별로 그룹화하여 집계
 *
 * @param assistDetails 어시스트별 상세 데이터
 * @returns 팀별 월간 데이터 배열
 */
export function groupByTeam(
  assistDetails: Record<string, AssistDetailData>
): TeamMonthlyData[] {
  const teamMap: Record<string, TeamMonthlyData> = {};

  for (const [key, detail] of Object.entries(assistDetails)) {
    const { teamName, receipts, expenses, profit } = detail;

    if (!teamMap[teamName]) {
      teamMap[teamName] = {
        teamName,
        monthlyReceipt: 0,
        monthlyExpense: 0,
        netProfit: 0,
        assistCount: 0
      };
    }

    teamMap[teamName].monthlyReceipt += receipts;
    teamMap[teamName].monthlyExpense += expenses;
    teamMap[teamName].netProfit += profit;
    teamMap[teamName].assistCount += 1;
  }

  const result = Object.values(teamMap).sort((a, b) => b.netProfit - a.netProfit);

  console.log(`[TeamMonthlyDataService] groupByTeam 완료: ${result.length}개 팀`);
  return result;
}

/**
 * 8. 팀별 실제매출 계산 (assistDetails에서 직접 계산)
 *
 * @param assistDetails 어시스트별 상세 데이터
 * @returns 팀별 실제매출 객체와 총 실제매출
 */
export function calculateTeamSalesFromAssistDetails(
  assistDetails: Record<string, AssistDetailData>
): { teamSales: Record<string, number>; totalSales: number } {
  const teamSales: Record<string, number> = {};
  let totalSales = 0;

  for (const [, detail] of Object.entries(assistDetails)) {
    const { teamName, profit } = detail;

    if (!teamSales[teamName]) {
      teamSales[teamName] = 0;
    }
    teamSales[teamName] += profit;
    totalSales += profit;
  }

  console.log(`[TeamMonthlyDataService] calculateTeamSalesFromAssistDetails 완료: ${Object.keys(teamSales).length}개 팀, 총 ${totalSales.toLocaleString()}원`);
  return { teamSales, totalSales };
}

// ═══════════════════════════════════════════════════
// 어시스트별 건수 집계 (MonthlyAssistantReport 하이브리드용)
// ═══════════════════════════════════════════════════

/**
 * 어시스트별 건수 데이터
 */
export interface AssistCountData {
  monthlyCount: number;     // 월별 건수
  dailyCount: number;       // 당일 건수
}

/**
 * assistReceipts 데이터 레코드 (Firebase dailyReports에서 로드)
 */
export interface DailyAssistReceiptsRecord {
  docDate: string;          // YYYY-MM-DD
  assistReceipts: Array<{
    assistName: string;
    count: number;
    amount: number;
    compositeKey?: string;
    cashierName?: string;
    assistTeam?: string;
  }>;
}

/**
 * 9. assistReceipts에서 어시스트별 건수 집계
 *
 * teamCollection에는 count 필드가 없으므로,
 * MonthlyAssistantReport의 건수 데이터를 위해 assistReceipts 사용
 *
 * @param dailyReports 일별 assistReceipts 데이터 배열
 * @param employeeTeamMap 직원-팀 매핑
 * @param targetDate 당일 날짜 (YYYY-MM-DD) - dailyCount 계산용
 * @returns 어시스트-팀 키별 건수 데이터
 */
export function aggregateAssistCounts(
  dailyReports: DailyAssistReceiptsRecord[],
  employeeTeamMap: Record<string, string>,
  targetDate: string
): Record<string, AssistCountData> {
  const countMap: Record<string, AssistCountData> = {};

  for (const report of dailyReports) {
    if (!report.assistReceipts || !Array.isArray(report.assistReceipts)) {
      continue;
    }

    for (const receipt of report.assistReceipts) {
      if (!receipt.assistName) continue;

      // compositeKey에서 원본 어시스트명/팀명 추출
      let assistName = receipt.assistName;
      let teamNameFromKey: string | undefined;

      if (receipt.compositeKey && receipt.compositeKey.includes('-')) {
        const [originalAssistName, originalTeamName] = receipt.compositeKey.split('-');
        if (originalAssistName) assistName = originalAssistName;
        if (originalTeamName) teamNameFromKey = originalTeamName;
      }

      // 5단계 팀 매핑 폴백
      const teamName = getTeamForAssist(
        assistName,
        employeeTeamMap,
        receipt.cashierName,
        receipt.assistTeam,
        teamNameFromKey,  // compositeKey에서 추출한 팀명
        undefined
      );

      // 키 생성 (MonthlyAssistantReport와 동일 형식: "어시스트명-팀명")
      const key = `${assistName}-${teamName}`;
      const count = receipt.count || 1;

      if (!countMap[key]) {
        countMap[key] = { monthlyCount: 0, dailyCount: 0 };
      }

      // 월별 건수 누적
      countMap[key].monthlyCount += count;

      // 당일 건수 (targetDate와 일치하는 경우만)
      if (report.docDate === targetDate) {
        countMap[key].dailyCount += count;
      }
    }
  }

  console.log(`[TeamMonthlyDataService] aggregateAssistCounts 완료: ${Object.keys(countMap).length}개 어시스트`);
  return countMap;
}

// ═══════════════════════════════════════════════════
// teamCollection 기반 금액 집계 (MonthlyAssistantReport 하이브리드용)
// ═══════════════════════════════════════════════════

/**
 * 어시스트별 금액 데이터 (teamCollection 기반)
 */
export interface AssistMonetaryData {
  assistName: string;
  teamName: string;
  monthlyReceipt: number;     // 월별 수납액
  monthlyExpense: number;     // 월별 지출액
  dailyReceipt: number;       // 당일 수납액
  dailyExpense: number;       // 당일 지출액
  compositeKey?: string;      // 참고용 최초 compositeKey (집계 전략은 출처별 MAX 맵으로 판정)
}

/**
 * 10. teamCollection에서 어시스트별 금액 집계
 *
 * ActualSalesService와 동일한 집계 전략 사용:
 * - Financial Transactions: += 합산
 * - 일반 teamCollection: Math.max() (월 누적값)
 *
 * @param yearMonth 조회할 년월 (YYYY-MM)
 * @param targetDate 당일 날짜 (YYYY-MM-DD) - dailyReceipt 계산용
 * @param employeeTeamMap 직원-팀 매핑
 * @param targetEmployees 대상 직원 배열 (TeamCollectionService용)
 * @returns 어시스트-팀 키별 금액 데이터
 */
export async function aggregateMonetaryFromTeamCollection(
  yearMonth: string,
  targetDate: string,
  employeeTeamMap: Record<string, string>,
  targetEmployees: Employee[]
): Promise<Record<string, AssistMonetaryData>> {
  // Firebase imports
  const { getDocs, collection } = await import('firebase/firestore');
  const { db } = await import('../firebase');
  const { TeamCollectionService } = await import('./teamCollectionService.ts');

  const reportsCol = collection(db, 'dailyReports');
  const snapshot = await getDocs(reportsCol);

  const processedCompositeKeys = new Set<string>();
  const monetaryMap: Record<string, AssistMonetaryData> = {};
  // 🛡️ 버킷별 출처(원본 compositeKey)별 MAX 맵 — 총합 = Σ(출처별 MAX) 보장용
  const sourceMaxByBucket: Record<string, SourceMaxMap> = {};
  const teamCollectionService = new TeamCollectionService();

  let processedDocs = 0;

  snapshot.docs.forEach(doc => {
    const docDate = doc.id; // YYYY-MM-DD

    if (docDate.startsWith(yearMonth)) {
      processedDocs++;
      const data = doc.data();

      // teamCollection 처리
      if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
        data.teamCollection.forEach((item: any) => {
          if (!item.isAssistRow || !item.compositeKey) return;

          // 중복 방지
          const dateCompositeKey = `${docDate}_${item.compositeKey}`;
          if (processedCompositeKeys.has(dateCompositeKey)) return;
          processedCompositeKeys.add(dateCompositeKey);

          // compositeKey에서 어시스트명 추출
          let assistName = item.assistName || '미지정';
          if (item.compositeKey && item.compositeKey.includes('-')) {
            const [keyAssistName] = item.compositeKey.split('-');
            if (keyAssistName) assistName = keyAssistName;
          }

          // 5단계 팀 매핑 폴백
          const isFinancialTransaction = item.isFinancialTransaction || false;
          let teamName = getTeamForAssist(
            assistName,
            employeeTeamMap,
            item.cashierName,
            isFinancialTransaction ? item.assistTeam : undefined,
            isFinancialTransaction ? item.teamName : undefined,
            item.team
          );

          // MonthlyAssistantReport와 동일한 키 형식: "어시스트명-팀명"
          const key = `${assistName}-${teamName}`;

          const monthlyReceipt = item.monthlyReceipt || 0;
          const monthlyExpense = item.monthlyExpense || 0;

          if (!monetaryMap[key]) {
            monetaryMap[key] = {
              assistName,
              teamName,
              monthlyReceipt: 0,
              monthlyExpense: 0,
              dailyReceipt: 0,
              dailyExpense: 0,
              compositeKey: item.compositeKey
            };
          }

          const existing = monetaryMap[key];

          if (isFinancialTransaction) {
            // ✅ Financial: 날짜별 개별 거래이므로 합산 (+=)
            existing.monthlyReceipt += monthlyReceipt;
            existing.monthlyExpense += monthlyExpense;
          } else {
            // 📊 일반: 같은 출처(원본 compositeKey)는 MAX, 다른 출처(팀 재매핑 전후 등)는 SUM
            // — 출처별 MAX 맵으로 총합 = Σ(출처별 MAX) 보장 ('직전 키 1개 기억'은 A→B→A 교차 도착 시 이중계상)
            const sources = sourceMaxByBucket[key] || (sourceMaxByBucket[key] = {});
            const delta = accumulateSourceMax(sources, item.compositeKey || '', monthlyReceipt, monthlyExpense);
            existing.monthlyReceipt += delta.receipts;
            existing.monthlyExpense += delta.expenses;
          }

          // 당일 데이터
          if (docDate === targetDate) {
            if (isFinancialTransaction) {
              existing.dailyReceipt += monthlyReceipt;
              existing.dailyExpense += monthlyExpense;
            } else {
              existing.dailyReceipt = Math.max(existing.dailyReceipt, monthlyReceipt);
              existing.dailyExpense = Math.max(existing.dailyExpense, monthlyExpense);
            }
          }
        });
      }

      // financialTransactions 처리 (teamCollection에 없는 경우 대비)
      if (data && data.financialTransactions && data.financialTransactions.items) {
        data.financialTransactions.items.forEach((transaction: any) => {
          const assistName = transaction.assistName || '미지정';
          const amount = transaction.amount || 0;

          // 5단계 팀 매핑 폴백
          const teamName = getTeamForAssist(
            assistName,
            employeeTeamMap,
            transaction.cashierName,
            transaction.assistTeam,
            transaction.teamName,
            transaction.team
          );

          const key = `${assistName}-${teamName}`;
          const isIncome = transaction.transactionType === 'income';
          const isExpense = transaction.transactionType === 'expense';

          if (!monetaryMap[key]) {
            monetaryMap[key] = {
              assistName,
              teamName,
              monthlyReceipt: 0,
              monthlyExpense: 0,
              dailyReceipt: 0,
              dailyExpense: 0
            };
          }

          // Financial은 합산
          if (isIncome) {
            monetaryMap[key].monthlyReceipt += amount;
            if (docDate === targetDate) {
              monetaryMap[key].dailyReceipt += amount;
            }
          } else if (isExpense) {
            monetaryMap[key].monthlyExpense += amount;
            if (docDate === targetDate) {
              monetaryMap[key].dailyExpense += amount;
            }
          }
        });
      }
    }
  });

  console.log(`[TeamMonthlyDataService] aggregateMonetaryFromTeamCollection 완료: ${Object.keys(monetaryMap).length}개 어시스트, ${processedDocs}개 문서 처리`);
  return monetaryMap;
}

/**
 * 11. 금액 데이터와 건수 데이터 병합
 *
 * @param monetaryData teamCollection 기반 금액 데이터
 * @param countData assistReceipts 기반 건수 데이터
 * @returns 병합된 어시스트별 데이터
 */
export function mergeMonetaryAndCountData(
  monetaryData: Record<string, AssistMonetaryData>,
  countData: Record<string, AssistCountData>
): Array<{
  assistName: string;
  teamName: string;
  monthlyReceipt: number;
  monthlyCount: number;
  dailyReceipt: number;
  dailyCount: number;
}> {
  const merged: Record<string, {
    assistName: string;
    teamName: string;
    monthlyReceipt: number;
    monthlyCount: number;
    dailyReceipt: number;
    dailyCount: number;
  }> = {};

  // 금액 데이터 먼저 처리
  for (const [key, monetary] of Object.entries(monetaryData)) {
    const counts = countData[key] || { monthlyCount: 0, dailyCount: 0 };

    // 순수익 계산 (수납 - 지출)
    const netMonthlyReceipt = monetary.monthlyReceipt - monetary.monthlyExpense;
    const netDailyReceipt = monetary.dailyReceipt - monetary.dailyExpense;

    merged[key] = {
      assistName: monetary.assistName,
      teamName: monetary.teamName,
      monthlyReceipt: netMonthlyReceipt,
      monthlyCount: counts.monthlyCount,
      dailyReceipt: netDailyReceipt,
      dailyCount: counts.dailyCount
    };
  }

  // 건수만 있고 금액이 없는 경우 추가
  for (const [key, counts] of Object.entries(countData)) {
    if (!merged[key] && counts.monthlyCount > 0) {
      const [assistName, teamName] = key.split('-');
      merged[key] = {
        assistName: assistName || '미지정',
        teamName: teamName || '미지정',
        monthlyReceipt: 0,
        monthlyCount: counts.monthlyCount,
        dailyReceipt: 0,
        dailyCount: counts.dailyCount
      };
    }
  }

  console.log(`[TeamMonthlyDataService] mergeMonetaryAndCountData 완료: ${Object.keys(merged).length}개 어시스트`);

  // 배열로 변환, 양수 필터링, 정렬
  return Object.values(merged)
    .filter(item => item.monthlyReceipt > 0)
    .sort((a, b) => b.monthlyReceipt - a.monthlyReceipt);
}
